import DashboardLayout from "@/components/DashboardLayout";
import { TabbedCodeBlock } from "@/components/TabbedCodeBlock";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { trackEvent } from "@/lib/analytics";
import { trpc } from "@/lib/trpc";
import { LANGUAGES, type Language, secretSnippets } from "@/snippets";
import { ArrowLeft, Copy, Key, Plus, Trash2, Clock, Activity as ActivityIcon, Shield, Globe, Calendar, TrendingUp, Ban, AlertTriangle, RefreshCw, Pencil, Check, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useLocation, useParams } from "wouter";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { Badge } from "@/components/ui/badge";

interface ActivityLog {
  id: number;
  secretId: number;
  tokenId: string | null;
  userId: number;
  action: string;
  timestamp: number;
  metadata: string | null;
}

function formatEventAction(action: string): string {
  const labels: Record<string, string> = {
    created: "Secret created",
    updated: "Secret updated",
    deleted: "Secret deleted",
    revoked: "Secret revoked",
    token_generated: "Token generated",
    token_revoked: "Token revoked",
    token_renewed: "Token renewed",
    ip_added: "IP added to whitelist",
    ip_removed: "IP removed from whitelist",
    proxy_created: "Proxy config created",
    env_created: ".ENV file created",
  };
  return labels[action] || action.replace(/_/g, " ");
}

function getEventLevel(action: string): string {
  if (["deleted", "revoked", "token_revoked", "ip_removed"].includes(action)) return "warn";
  if (["created", "token_generated", "token_renewed", "proxy_created", "env_created"].includes(action)) return "success";
  if (["ip_added"].includes(action)) return "info";
  return "info";
}

function TokenCard({ token, isExpired, totalUsage, formatTTL, editingNameTokenId, editNameValue, setEditingNameTokenId, setEditNameValue, updateTokenNameMutation, tokenIpDialogId, setTokenIpDialogId, tokenIpAddress, setTokenIpAddress, tokenIpDesc, setTokenIpDesc, addTokenIpMutation, removeTokenIpMutation, savedIps }: any) {
  const { data: tokenIps, refetch: refetchTokenIps } = trpc.tokens.listIpWhitelist.useQuery({ tokenId: token.id });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            {editingNameTokenId === token.id ? (
              <div className="flex items-center gap-2 mb-1">
                <Input
                  value={editNameValue}
                  onChange={(e) => setEditNameValue(e.target.value)}
                  placeholder="Token name"
                  className="h-8 text-sm max-w-[200px]"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") updateTokenNameMutation.mutate({ tokenId: token.id, name: editNameValue });
                    if (e.key === "Escape") setEditingNameTokenId(null);
                  }}
                  autoFocus
                />
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => updateTokenNameMutation.mutate({ tokenId: token.id, name: editNameValue })}>
                  <Check className="w-3 h-3" />
                </Button>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingNameTokenId(null)}>
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2 mb-1">
                {token.name ? (
                  <CardTitle className="text-base">{token.name}</CardTitle>
                ) : (
                  <CardTitle className="text-base font-mono text-muted-foreground">
                    {token.token.substring(0, 20)}...
                  </CardTitle>
                )}
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6"
                  onClick={() => {
                    setEditingNameTokenId(token.id);
                    setEditNameValue(token.name || "");
                  }}
                >
                  <Pencil className="w-3 h-3" />
                </Button>
              </div>
            )}
            {token.name && (
              <p className="text-xs font-mono text-muted-foreground">{token.token.substring(0, 20)}...</p>
            )}
            <CardDescription className="mt-1">
              Created {new Date(token.createdAt).toLocaleString()}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {(token.renewalCount ?? 0) > 0 && (
              <div className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-blue-500/10 text-blue-600 dark:text-blue-400">
                <RefreshCw className="w-3 h-3" />
                {token.renewalCount} renewal{token.renewalCount !== 1 ? "s" : ""}
              </div>
            )}
            <div className={`px-2 py-1 rounded text-xs font-medium ${
              isExpired
                ? "bg-destructive/10 text-destructive"
                : "bg-primary/10 text-primary"
            }`}>
              {isExpired ? "Expired" : "Active"}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-muted-foreground">TTL</p>
            <p className="font-medium">{formatTTL(token.ttlSeconds)}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Expires</p>
            <p className="font-medium">{new Date(token.expiresAt).toLocaleString()}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Usage Count</p>
            <p className="font-medium">
              {token.usageCount}
              {(token.inheritedUsageCount ?? 0) > 0 && (
                <span className="text-muted-foreground ml-1">
                  ({totalUsage} total across lineage)
                </span>
              )}
            </p>
          </div>
          {token.lastUsedAt && (
            <div>
              <p className="text-muted-foreground">Last Used</p>
              <p className="font-medium">{new Date(token.lastUsedAt).toLocaleString()}</p>
            </div>
          )}
        </div>

        {/* Token IP Whitelist */}
        <div className="border-t pt-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">Token IP Whitelist</span>
              {tokenIps && tokenIps.length > 0 && (
                <Badge variant="secondary" className="text-xs">{tokenIps.length}</Badge>
              )}
            </div>
            <Dialog open={tokenIpDialogId === token.id} onOpenChange={(open) => setTokenIpDialogId(open ? token.id : null)}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="h-7 text-xs">
                  <Plus className="w-3 h-3 mr-1" />
                  Add IP
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add IP to Token Whitelist</DialogTitle>
                  <DialogDescription>
                    Restrict this specific token to only work from certain IPs.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  {savedIps && savedIps.length > 0 && (
                    <div className="space-y-2">
                      <Label>Select from Saved IPs</Label>
                      <Select
                        onValueChange={(value) => {
                          const selected = savedIps.find((ip: any) => ip.id.toString() === value);
                          if (selected) {
                            setTokenIpAddress(selected.ipAddress);
                            setTokenIpDesc(selected.name);
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choose a saved IP..." />
                        </SelectTrigger>
                        <SelectContent>
                          {savedIps.map((ip: any) => (
                            <SelectItem key={ip.id} value={ip.id.toString()}>
                              <span className="font-medium">{ip.name}</span>
                              <span className="text-muted-foreground font-mono text-sm ml-2">({ip.ipAddress})</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>IP Address</Label>
                    <Input
                      placeholder="e.g., 192.168.1.1"
                      value={tokenIpAddress}
                      onChange={(e) => setTokenIpAddress(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description (optional)</Label>
                    <Input
                      placeholder="e.g., CI Server"
                      value={tokenIpDesc}
                      onChange={(e) => setTokenIpDesc(e.target.value)}
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        const response = await fetch('https://api.ipify.org?format=json');
                        const data = await response.json();
                        setTokenIpAddress(data.ip);
                        toast.success(`Your IP: ${data.ip}`);
                      } catch {
                        toast.error("Failed to detect IP");
                      }
                    }}
                  >
                    <Globe className="w-3 h-3 mr-1" />
                    Detect My IP
                  </Button>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setTokenIpDialogId(null)}>Cancel</Button>
                  <Button
                    onClick={() => {
                      if (!tokenIpAddress.trim()) { toast.error("Enter an IP"); return; }
                      addTokenIpMutation.mutate({
                        tokenId: token.id,
                        ipAddress: tokenIpAddress.trim(),
                        description: tokenIpDesc.trim() || undefined,
                      }, {
                        onSuccess: () => refetchTokenIps(),
                      });
                    }}
                    disabled={addTokenIpMutation.isPending}
                  >
                    {addTokenIpMutation.isPending ? "Adding..." : "Add IP"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          {tokenIps && tokenIps.length > 0 ? (
            <div className="space-y-1">
              {tokenIps.map((ip: any) => (
                <div key={ip.id} className="flex items-center justify-between py-1 px-2 rounded bg-muted/50 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs">{ip.ipAddress}</span>
                    {ip.description && <span className="text-muted-foreground text-xs">- {ip.description}</span>}
                  </div>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6"
                    onClick={() => {
                      if (confirm("Remove this IP from the token whitelist?")) {
                        removeTokenIpMutation.mutate({ ipId: ip.id, tokenId: token.id }, {
                          onSuccess: () => refetchTokenIps(),
                        });
                      }
                    }}
                  >
                    <Trash2 className="w-3 h-3 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">No IP restrictions - all IPs allowed for this token.</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function SecretDetail() {
  const { id } = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const secretId = parseInt(id || "0");

  const [isGenerateOpen, setIsGenerateOpen] = useState(false);
  const [ttlSeconds, setTtlSeconds] = useState("3600");
  const [tokenName, setTokenName] = useState("");
  const [generatedToken, setGeneratedToken] = useState<string | null>(null);
  const [newIp, setNewIp] = useState("");
  const [newIpDescription, setNewIpDescription] = useState("");
  const [isAddIpOpen, setIsAddIpOpen] = useState(false);
  const [tokenIpDialogId, setTokenIpDialogId] = useState<string | null>(null);
  const [tokenIpAddress, setTokenIpAddress] = useState("");
  const [tokenIpDesc, setTokenIpDesc] = useState("");
  const [editingNameTokenId, setEditingNameTokenId] = useState<string | null>(null);
  const [editNameValue, setEditNameValue] = useState("");

  const { data: secret, isLoading, refetch: refetchSecret } = trpc.secrets.get.useQuery({ id: secretId });
  const { data: tokens, refetch: refetchTokens } = trpc.tokens.list.useQuery({ secretId });
  const { data: activity, refetch: refetchActivity } = trpc.secrets.getActivity.useQuery({ secretId, daysBack: 30 });
  const { data: ipWhitelist, refetch: refetchIpWhitelist } = trpc.ipWhitelist.list.useQuery({ secretId });
  const { data: ipStats } = trpc.secrets.getIpStats.useQuery({ secretId, daysBack: 30 });
  const { data: savedIps } = trpc.savedIps.list.useQuery();
  const [selectedSavedIp, setSelectedSavedIp] = useState<string>("");

  const generateMutation = trpc.tokens.generate.useMutation({
    onSuccess: (data) => {
      trackEvent("token_generated");
      toast.success("Token generated successfully");
      setGeneratedToken(data.token);
      refetchTokens();
      refetchActivity();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const deleteMutation = trpc.secrets.delete.useMutation({
    onSuccess: () => {
      trackEvent("secret_deleted");
      toast.success("Secret deleted successfully");
      setLocation("/secrets");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const revokeMutation = trpc.secrets.revoke.useMutation({
    onSuccess: () => {
      toast.success("Secret revoked successfully");
      refetchSecret();
      refetchActivity();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const addIpMutation = trpc.ipWhitelist.add.useMutation({
    onSuccess: () => {
      toast.success("IP added to whitelist");
      setNewIp("");
      setNewIpDescription("");
      setIsAddIpOpen(false);
      refetchIpWhitelist();
      refetchActivity();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const removeIpMutation = trpc.ipWhitelist.remove.useMutation({
    onSuccess: () => {
      toast.success("IP removed from whitelist");
      refetchIpWhitelist();
      refetchActivity();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const addTokenIpMutation = trpc.tokens.addIpToWhitelist.useMutation({
    onSuccess: () => {
      toast.success("IP added to token whitelist");
      setTokenIpAddress("");
      setTokenIpDesc("");
      setTokenIpDialogId(null);
      refetchTokens();
    },
    onError: (error) => toast.error(error.message),
  });

  const removeTokenIpMutation = trpc.tokens.removeIpFromWhitelist.useMutation({
    onSuccess: () => {
      toast.success("IP removed from token whitelist");
      refetchTokens();
    },
    onError: (error) => toast.error(error.message),
  });

  const updateTokenNameMutation = trpc.tokens.updateName.useMutation({
    onSuccess: () => {
      toast.success("Token name updated");
      setEditingNameTokenId(null);
      refetchTokens();
    },
    onError: (error) => toast.error(error.message),
  });

  const handleGenerate = () => {
    const ttl = parseInt(ttlSeconds);
    if (isNaN(ttl) || ttl < 60 || ttl > 86400 * 30) {
      toast.error("TTL must be between 60 seconds and 30 days");
      return;
    }

    generateMutation.mutate({
      secretId,
      ttlSeconds: ttl,
      name: tokenName.trim() || undefined,
    });
  };

  const handleCopyToken = (token: string) => {
    navigator.clipboard.writeText(token);
    toast.success("Token copied to clipboard");
  };

  const handleDelete = () => {
    if (confirm("Are you sure you want to delete this secret? This action cannot be undone.")) {
      deleteMutation.mutate({ id: secretId });
    }
  };

  const handleAddIp = () => {
    if (!newIp.trim()) {
      toast.error("Please enter an IP address");
      return;
    }
    addIpMutation.mutate({
      secretId,
      ipAddress: newIp.trim(),
      description: newIpDescription.trim() || undefined,
    });
  };

  const handleRemoveIp = (ipId: number) => {
    if (confirm("Are you sure you want to remove this IP from the whitelist?")) {
      removeIpMutation.mutate({ ipId, secretId });
    }
  };

  const formatTTL = (seconds: number) => {
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours`;
    return `${Math.floor(seconds / 86400)} days`;
  };

  const snippetConfig = {
    token: "YOUR_TOKEN_HERE",
    baseUrl: window.location.origin,
  };

  // Prepare chart data from dailyStats
  const chartData = activity?.dailyStats 
    ? Object.entries(activity.dailyStats)
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => a.date.localeCompare(b.date))
    : [];

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-12">
          <Spinner className="size-8 text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (!secret) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold mb-2">Secret not found</h2>
          <Button onClick={() => setLocation("/secrets")}>Back to Secrets</Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => setLocation("/secrets")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold text-foreground">{secret.name}</h1>
              {secret.isRevoked && (
                <Badge variant="destructive" className="flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Revoked
                </Badge>
              )}
            </div>
            {secret.description && (
              <p className="text-muted-foreground mt-1">{secret.description}</p>
            )}
          </div>
          <div className="flex gap-2">
            {!secret.isRevoked && (
              <Button variant="outline" onClick={() => revokeMutation.mutate({ id: secretId })}>
                <Ban className="w-4 h-4 mr-2" />
                Revoke
              </Button>
            )}
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>

        <Tabs defaultValue="tokens" className="space-y-4">
          <TabsList>
            <TabsTrigger value="tokens">Tokens</TabsTrigger>
            <TabsTrigger value="activity">Activity</TabsTrigger>
            <TabsTrigger value="events">Events</TabsTrigger>
            <TabsTrigger value="security">Security</TabsTrigger>
            <TabsTrigger value="usage">How to Use</TabsTrigger>
          </TabsList>

          <TabsContent value="tokens" className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Access Tokens</h3>
                <p className="text-sm text-muted-foreground">Generate short-lived tokens to access this secret</p>
              </div>
              <Dialog open={isGenerateOpen} onOpenChange={setIsGenerateOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="w-4 h-4 mr-2" />
                    Generate Token
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Generate Access Token</DialogTitle>
                    <DialogDescription>
                      Create a short-lived token to access this secret. The token will expire after the specified TTL.
                    </DialogDescription>
                  </DialogHeader>
                  {generatedToken ? (
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Your Token</Label>
                        <div className="flex gap-2">
                          <Input
                            value={generatedToken}
                            readOnly
                            className="font-mono text-sm"
                          />
                          <Button size="icon" onClick={() => handleCopyToken(generatedToken)}>
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Copy this token now. You won't be able to see it again.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="tokenName">Name (optional)</Label>
                        <Input
                          id="tokenName"
                          placeholder="e.g., Production CI, Local Dev"
                          value={tokenName}
                          onChange={(e) => setTokenName(e.target.value)}
                        />
                        <p className="text-xs text-muted-foreground">Give this token a name to identify which code or environment uses it.</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="ttl">Time to Live (TTL)</Label>
                        <Select value={ttlSeconds} onValueChange={setTtlSeconds}>
                          <SelectTrigger id="ttl">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="3600">1 hour</SelectItem>
                            <SelectItem value="21600">6 hours</SelectItem>
                            <SelectItem value="86400">1 day</SelectItem>
                            <SelectItem value="604800">7 days</SelectItem>
                            <SelectItem value="2592000">30 days</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                  <DialogFooter>
                    {generatedToken ? (
                      <Button onClick={() => {
                        setIsGenerateOpen(false);
                        setGeneratedToken(null);
                        setTokenName("");
                      }}>
                        Done
                      </Button>
                    ) : (
                      <>
                        <Button variant="outline" onClick={() => setIsGenerateOpen(false)}>
                          Cancel
                        </Button>
                        <Button onClick={handleGenerate} disabled={generateMutation.isPending}>
                          {generateMutation.isPending ? "Generating..." : "Generate"}
                        </Button>
                      </>
                    )}
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            {tokens && tokens.length > 0 ? (
              <div className="grid gap-4">
                {tokens.map((token) => {
                  const isExpired = new Date(token.expiresAt) < new Date();
                  const totalUsage = (token.inheritedUsageCount ?? 0) + token.usageCount;
                  return (
                    <TokenCard
                      key={token.id}
                      token={token}
                      isExpired={isExpired}
                      totalUsage={totalUsage}
                      formatTTL={formatTTL}
                      editingNameTokenId={editingNameTokenId}
                      editNameValue={editNameValue}
                      setEditingNameTokenId={setEditingNameTokenId}
                      setEditNameValue={setEditNameValue}
                      updateTokenNameMutation={updateTokenNameMutation}
                      tokenIpDialogId={tokenIpDialogId}
                      setTokenIpDialogId={setTokenIpDialogId}
                      tokenIpAddress={tokenIpAddress}
                      setTokenIpAddress={setTokenIpAddress}
                      tokenIpDesc={tokenIpDesc}
                      setTokenIpDesc={setTokenIpDesc}
                      addTokenIpMutation={addTokenIpMutation}
                      removeTokenIpMutation={removeTokenIpMutation}
                      savedIps={savedIps}
                    />
                  );
                })}
              </div>
            ) : (
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon"><Key /></EmptyMedia>
                  <EmptyTitle>No tokens yet</EmptyTitle>
                  <EmptyDescription>
                    Generate a token to start accessing this secret in your code.
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            )}
          </TabsContent>

          <TabsContent value="activity" className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-2">API Access Activity</h3>
              <p className="text-sm text-muted-foreground mb-4">Track API calls to this secret over time</p>
              
              {/* IP Access Distribution Pie Chart */}
              {ipStats && ipStats.length > 0 && (
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      Access by IP Address (Last 30 Days)
                    </CardTitle>
                    <CardDescription>Distribution of API calls by source IP</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={ipStats.map((stat: { ip: string; count: number }) => ({
                              name: stat.ip,
                              value: stat.count,
                            }))}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {ipStats.map((_: any, index: number) => (
                              <Cell key={`cell-${index}`} fill={['#8b7355', '#6b5d4f', '#4a4238', '#3a342a', '#2a2520'][index % 5]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Usage Graph */}
              {chartData.length > 0 ? (
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      Usage Over Time (Last 30 Days)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis 
                            dataKey="date" 
                            tick={{ fontSize: 12 }}
                            tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Tooltip 
                            labelFormatter={(value) => new Date(value).toLocaleDateString()}
                            formatter={(value: number) => [value, 'API Calls']}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="count" 
                            stroke="hsl(var(--primary))" 
                            strokeWidth={2}
                            dot={{ fill: "hsl(var(--primary))" }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Empty className="mb-6">
                  <EmptyHeader>
                    <EmptyMedia variant="icon"><TrendingUp /></EmptyMedia>
                    <EmptyDescription>
                      No API activity yet. Usage graph will appear here once you start accessing this secret.
                    </EmptyDescription>
                  </EmptyHeader>
                </Empty>
              )}

              {/* Recent Activity List */}
              <h4 className="text-base font-medium mb-3">Recent API Calls</h4>
              {activity && activity.activity.length > 0 ? (
                <div className="space-y-2">
                  {activity.activity.map((log: ActivityLog) => (
                    <Card key={log.id}>
                      <CardContent className="py-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <ActivityIcon className="w-4 h-4 text-primary" />
                            <span className="font-medium">Secret accessed</span>
                          </div>
                          <span className="text-sm text-muted-foreground">
                            {new Date(log.timestamp).toLocaleString()}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Empty>
                  <EmptyHeader>
                    <EmptyMedia variant="icon"><Clock /></EmptyMedia>
                    <EmptyDescription>
                      No API calls yet. Activity will appear here once you start using this secret.
                    </EmptyDescription>
                  </EmptyHeader>
                </Empty>
              )}
            </div>
          </TabsContent>

          <TabsContent value="events" className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-2">Events Log</h3>
              <p className="text-sm text-muted-foreground mb-4">Track key creation, updates, and configuration changes</p>

              {activity && activity.events.length > 0 ? (
                <div className="rounded-lg border border-border bg-white dark:bg-zinc-950 overflow-hidden font-mono text-sm">
                  <div className="px-4 py-2 border-b border-border bg-zinc-50 dark:bg-zinc-900 flex items-center gap-2">
                    <div className="flex gap-1.5">
                      <span className="w-3 h-3 rounded-full bg-red-400" />
                      <span className="w-3 h-3 rounded-full bg-yellow-400" />
                      <span className="w-3 h-3 rounded-full bg-green-400" />
                    </div>
                    <span className="text-xs text-zinc-500 ml-2">events.log</span>
                  </div>
                  <div className="divide-y divide-border/50">
                    {activity.events.map((log: ActivityLog, i: number) => {
                      const date = new Date(log.timestamp);
                      const ts = date.toISOString().replace('T', ' ').substring(0, 19);
                      const label = formatEventAction(log.action);
                      const level = getEventLevel(log.action);
                      const meta = log.metadata ? (() => { try { return JSON.parse(log.metadata!); } catch { return null; } })() : null;
                      return (
                        <div key={log.id} className="px-4 py-2 hover:bg-zinc-50 dark:hover:bg-zinc-900/50 transition-colors">
                          <div className="flex items-start gap-3 text-zinc-900 dark:text-zinc-100">
                            <span className="text-zinc-400 dark:text-zinc-600 shrink-0 select-none tabular-nums">{String(i + 1).padStart(3, ' ')}</span>
                            <span className="text-zinc-500 dark:text-zinc-500 shrink-0 tabular-nums">{ts}</span>
                            <span className={`shrink-0 font-semibold ${
                              level === 'warn' ? 'text-amber-600 dark:text-amber-400' :
                              level === 'error' ? 'text-red-600 dark:text-red-400' :
                              level === 'success' ? 'text-emerald-600 dark:text-emerald-400' :
                              'text-blue-600 dark:text-blue-400'
                            }`}>[{level.toUpperCase().padEnd(7)}]</span>
                            <span className="text-zinc-900 dark:text-zinc-100">{label}</span>
                            {meta && (
                              <span className="text-zinc-400 dark:text-zinc-600 truncate">
                                {Object.entries(meta).map(([k, v]) => `${k}=${v}`).join(' ')}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <Empty>
                  <EmptyHeader>
                    <EmptyMedia variant="icon"><Calendar /></EmptyMedia>
                    <EmptyTitle>No events yet</EmptyTitle>
                    <EmptyDescription>
                      Events like token generation and IP changes will appear here.
                    </EmptyDescription>
                  </EmptyHeader>
                </Empty>
              )}
            </div>
          </TabsContent>

          <TabsContent value="security" className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">IP Whitelist</h3>
                <p className="text-sm text-muted-foreground">
                  Restrict access to specific IP addresses. Leave empty to allow all IPs.
                </p>
              </div>
              <Dialog open={isAddIpOpen} onOpenChange={setIsAddIpOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="w-4 h-4 mr-2" />
                    Add IP
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Add IP to Whitelist</DialogTitle>
                    <DialogDescription>
                      Add an IP address that will be allowed to access this secret.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    {/* Saved IPs Dropdown */}
                    {savedIps && savedIps.length > 0 && (
                      <div className="space-y-2">
                        <Label>Select from Saved IPs</Label>
                        <Select
                          value={selectedSavedIp}
                          onValueChange={(value) => {
                            setSelectedSavedIp(value);
                            const selected = savedIps.find(ip => ip.id.toString() === value);
                            if (selected) {
                              setNewIp(selected.ipAddress);
                              setNewIpDescription(selected.name);
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Choose a saved IP..." />
                          </SelectTrigger>
                          <SelectContent>
                            {savedIps.map((ip) => (
                              <SelectItem key={ip.id} value={ip.id.toString()}>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{ip.name}</span>
                                  <span className="text-muted-foreground font-mono text-sm">({ip.ipAddress})</span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Or enter a new IP address below
                        </p>
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      <Label htmlFor="ip">IP Address</Label>
                      <Input
                        id="ip"
                        placeholder="e.g., 192.168.1.1"
                        value={newIp}
                        onChange={(e) => {
                          setNewIp(e.target.value);
                          setSelectedSavedIp(""); // Clear selection when manually typing
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ipDesc">Description (optional)</Label>
                      <Input
                        id="ipDesc"
                        placeholder="e.g., Office network"
                        value={newIpDescription}
                        onChange={(e) => setNewIpDescription(e.target.value)}
                      />
                    </div>
                    
                    {/* Find My IP Section */}
                    <div className="border-t pt-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Globe className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-medium">Find My IP</span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          try {
                            const response = await fetch('https://api.ipify.org?format=json');
                            const data = await response.json();
                            setNewIp(data.ip);
                            setSelectedSavedIp("");
                            toast.success(`Your IP: ${data.ip}`);
                          } catch {
                            toast.error("Failed to detect IP address");
                          }
                        }}
                      >
                        Detect My Current IP
                      </Button>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsAddIpOpen(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleAddIp} disabled={addIpMutation.isPending}>
                      {addIpMutation.isPending ? "Adding..." : "Add IP"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            {ipWhitelist && ipWhitelist.length > 0 ? (
              <div className="grid gap-4">
                {ipWhitelist.map((ip) => (
                  <Card key={ip.id}>
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Shield className="w-5 h-5 text-primary" />
                          <div>
                            <p className="font-mono font-medium">{ip.ipAddress}</p>
                            {ip.description && (
                              <p className="text-sm text-muted-foreground">{ip.description}</p>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveIp(ip.id)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon"><Shield /></EmptyMedia>
                  <EmptyTitle>No IP restrictions</EmptyTitle>
                  <EmptyDescription>
                    All IP addresses are currently allowed to access this secret. Add IPs to restrict access.
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            )}
          </TabsContent>

          <TabsContent value="usage" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>How to Use This Secret</CardTitle>
                <CardDescription>
                  Use the following Python code snippet in your Jupyter notebook to fetch this secret securely.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg border">
                  <span className="text-sm text-muted-foreground">First, install the package:</span>
                  <code className="px-2 py-1 bg-muted rounded text-sm font-mono">pip install double-o</code>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2"
                    onClick={() => {
                      navigator.clipboard.writeText("pip install double-o");
                      toast.success("Copied to clipboard");
                    }}
                  >
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
                <div>
                  <Label className="mb-2 block">Code Snippet</Label>
                  <TabbedCodeBlock snippets={Object.fromEntries(
                    LANGUAGES.map(({ value }) => [value, secretSnippets[value](snippetConfig)])
                  ) as Record<Language, string>} />
                </div>
                
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Steps:</h4>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                    <li>Generate a token from the "Tokens" tab above</li>
                    <li>Copy the token and replace "YOUR_TOKEN_HERE" in the code</li>
                    <li>Run the code in your Jupyter notebook</li>
                    <li>The secret value will be fetched securely</li>
                  </ol>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
